import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchWithAuth, authenticatedFetch, API_ENDPOINT } from '../utils/api';
import { AlertTriangle, Activity, Search, X, FileText, CheckCircle, RefreshCw, Shield, Package, Calendar, XCircle, ChevronDown, ChevronRight, TrendingUp, Users, Building, BookOpen, Radio, Download } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { Widget, MISSION_AREAS, groupCapabilitiesByMissionArea } from './Common';

// Mission Area Section Component (collapsible group)
function CollapsibleMissionAreaSection({ missionAreaKey, missionAreaData, capabilities, isExpanded, onToggle }) {
  if (!capabilities || Object.keys(capabilities).length === 0) {
    return null;
  }

  const getScoreColor = (score) => {
    if (score < 60) return 'bg-red-500';
    if (score < 75) return 'bg-orange-500';
    if (score < 85) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getScoreBgColor = (score) => {
    if (score < 60) return 'bg-red-100 border-red-300 text-red-900';
    if (score < 75) return 'bg-orange-100 border-orange-300 text-orange-900';
    if (score < 85) return 'bg-yellow-100 border-yellow-300 text-yellow-900';
    return 'bg-green-100 border-green-300 text-green-900';
  };

  return (
    <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
      {/* Header - Clickable */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition"
      >
        <div className="flex items-center space-x-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronRight className="w-5 h-5 text-blue-600" />
          )}
          <div className="text-left">
            <h3 className="font-bold text-gray-900 text-lg">{missionAreaData.name}</h3>
            <p className="text-sm text-gray-600">{Object.keys(capabilities).length} capabilities</p>
          </div>
        </div>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="p-6 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(capabilities)
              .sort((a, b) => a[1].avg_score - b[1].avg_score)
              .map(([capName, data]) => {
                // Check if this capability has data
                const hasData = data.count > 0 && data.avg_score > 0;

                if (!hasData) {
                  // Show grey "No data" card
                  return (
                    <div key={capName} className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-4 h-full">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-400 line-clamp-2">{capName}</h4>
                      </div>
                      <div className="text-center py-4">
                        <p className="text-xs text-gray-400">No data available</p>
                      </div>
                    </div>
                  );
                }

                // Show normal capability card with data
                return (
                  <div
                    key={capName}
                    className={`p-4 rounded-lg border-2 ${getScoreBgColor(data.avg_score)} hover:shadow-lg transition-all cursor-pointer`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-semibold text-sm leading-tight pr-2">{capName}</h4>
                      <div className="flex-shrink-0">
                        <div className={`text-2xl font-bold ${
                          data.avg_score < 60 ? 'text-red-600' :
                          data.avg_score < 75 ? 'text-orange-600' :
                          data.avg_score < 85 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {data.avg_score.toFixed(0)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-3 rounded-full transition-all duration-500 ${getScoreColor(data.avg_score)}`}
                          style={{ width: `${Math.min(data.avg_score, 100)}%` }}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-600">
                          {data.count} incident{data.count !== 1 ? 's' : ''}
                        </span>
                        <span className="font-medium">
                          {data.avg_score < 60 ? 'Critical' :
                           data.avg_score < 75 ? 'Needs Work' :
                           data.avg_score < 85 ? 'Adequate' :
                           'Strong'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

const SEVERITY_CFG = {
  Critical: { bg: '#fee2e2', border: '#ef4444', badge: '#dc2626' },
  High:     { bg: '#ffedd5', border: '#f97316', badge: '#ea580c' },
  Medium:   { bg: '#fef9c3', border: '#eab308', badge: '#ca8a04' },
  Low:      { bg: '#dcfce7', border: '#22c55e', badge: '#16a34a' },
};

const POETE_ICONS = {
  Planning:     '📋',  // maps to old Personnel/Policy gaps
  Organization: '👥',  // maps to old Personnel
  Equipment:    '🔧',  // same as before
  Training:     '🎓',  // same concept, cleaner icon
  Exercises:    '⚠️',  // new
};

const POETE_COLORS = {
  Planning:     { color: '#7c3aed', bg: '#ede9fe' },
  Organization: { color: '#2563eb', bg: '#dbeafe' },
  Equipment:    { color: '#d97706', bg: '#fef3c7' },
  Training:     { color: '#059669', bg: '#d1fae5' },
  Exercises:    { color: '#dc2626', bg: '#fee2e2' },
};
const RESOURCE_ICONS = {
  Personnel: '👥', Equipment: '🔧', Facility: '🏢',
  Training: '📋', Communication: '📡', Other: '⚠️',
};

const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low'];

function deriveFallbackTitle(description = '') {
  const words = description.replace(/[^a-zA-Z0-9 ]/g, '').split(' ').filter(Boolean);
  return words.slice(0, 5).join(' ') + (words.length > 5 ? '…' : '');
}

function GapCardView({ gaps, onGapClick, filterType }) {
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setShowAll(false);
  }, [filterType]);

  const sorted = [...gaps].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
  );

  const displayed = showAll ? sorted : sorted.slice(0, 12);

  return (
    <div>
      {/* Severity summary pills */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {SEVERITY_ORDER.map(level => {
          const count = gaps.filter(g => g.severity === level).length;
          if (!count) return null;
          const cfg = SEVERITY_CFG[level];
          return (
            <div key={level} style={{
              background: cfg.bg, border: `1px solid ${cfg.border}`,
              borderRadius: '20px', padding: '3px 12px',
              fontSize: '11.5px', fontWeight: '600', color: cfg.badge,
            }}>
              {count} {level}
            </div>
          );
        })}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#bbb', fontStyle: 'italic' }}>
          click any card for details
        </span>
      </div>

      {/* Cards grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4,1fr)',
        gap: '10px',
      }}>
        {displayed.map((gap, idx) => {
          const cfg = SEVERITY_CFG[gap.severity] || SEVERITY_CFG.Medium;
          const icon = RESOURCE_ICONS[gap.resource_type] || '⚠️';
          const title = gap.gap_title || deriveFallbackTitle(gap.gap_description);
          const frequency = gap.frequency || gap.occurrence_count || gap.occurrences?.length || 1;

          return (
            <div
              key={gap.gap_id || idx}
              onClick={() => onGapClick(gap)}
              className="cursor-pointer"
              style={{
                background: cfg.bg,
                border: `1.5px solid ${cfg.border}`,
                borderRadius: '12px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                position: 'relative',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = `0 6px 16px rgba(0,0,0,0.10)`;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Icon + severity badge */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '18px' }}>{icon}</span>
                <span style={{
                  background: cfg.badge,
                  color: '#fff',
                  fontSize: '10px', fontWeight: '700',
                  padding: '2px 8px', borderRadius: '99px',
                  letterSpacing: '0.03em',
                }}>
                  {gap.severity || 'Medium'}
                </span>
              </div>

              {/* Title */}
              <div style={{
                fontWeight: '600',
                fontSize: '13.5px',
                color: '#111827',
                lineHeight: '1.4',
                letterSpacing: '-0.01em',
                marginTop: '2px',
              }}>
                {title}
              </div>

              {/* FEMA capability */}
              <div style={{
                fontSize: '11px',
                color: '#6b7280',
                fontWeight: '500',
                lineHeight: '1.3',
              }}>
                {gap.nims_capability}
              </div>

              {/* Footer row — status hint + frequency bubble */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: '6px',
              }}>
                <span style={{
                  fontSize: '10.5px',
                  color: gap.status === 'resolved' ? '#16a34a' : '#9ca3af',
                  fontWeight: gap.status === 'resolved' ? '600' : '400',
                }}>
                  {gap.status === 'resolved' ? '✓ Resolved' : 'Click for details →'}
                </span>

                {/* Frequency circle */}
                <div style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  background: frequency >= 5 ? cfg.badge :
                               frequency >= 3 ? cfg.badge :
                               frequency >= 2 ? cfg.badge : cfg.badge,
                  border: `2px solid ${cfg.border}`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  opacity: 0.85,
                }}>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '800',
                    lineHeight: 1,
                    color: '#fff',
                  }}>
                    {frequency}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show All / Show Less toggle */}
      {gaps.length > 12 && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={() => setShowAll(prev => !prev)}
            className="px-5 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
          >
            {showAll ? 'Show Top 12' : `Show All ${gaps.length} Gaps`}
          </button>
        </div>
      )}
    </div>
  );
}

function CapabilityGapsAnalysisRefined({ 
  gaps, 
  onGapClick, 
  onStatusUpdate,
  resourceSummary,
  capabilitySummary 
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('active');
  const [displayLimit, setDisplayLimit] = useState(10);
  const [viewMode, setViewMode] = useState('list');
  
  if (!gaps) {
    return (
      <Widget
        title="Capability Gaps Analysis"
        subtitle="Loading..."
        icon={<AlertTriangle className="w-5 h-5" />}
      >
        <div className="text-center py-12">
          <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300 animate-spin" />
          <p className="text-gray-500">Loading capability gaps...</p>
        </div>
      </Widget>
    );
  }

  const safeGaps = Array.isArray(gaps) ? gaps : [];
  
  const filteredGaps = safeGaps.filter(gap => {
    const matchesSearch = searchTerm === '' || 
      gap.gap_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gap.resource_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      gap.nims_capability?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'active' && gap.status === 'active') ||
      (filterType === 'resolved' && gap.status === 'resolved');
    
    return matchesSearch && matchesFilter;
  });

  const displayedGaps = displayLimit === 'all' 
    ? filteredGaps 
    : filteredGaps.slice(0, displayLimit);

  const activeCount = safeGaps.filter(g => g.status === 'active').length;
  const resolvedCount = safeGaps.filter(g => g.status === 'resolved').length;

  const safeResourceSummary = resourceSummary || {};
  const topResourceTypes = Object.entries(safeResourceSummary)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([type]) => type);

  return (
    <Widget
      title="Capability Gaps Analysis"
      subtitle={`${activeCount} active gaps • ${resolvedCount} resolved`}
      icon={<AlertTriangle className="w-5 h-5" />}
    >
      {/* View Mode Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-gray-600 font-medium">Display Mode</span>
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-1.5 text-sm font-medium transition border-l border-gray-200 ${
              viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            ☰ List
          </button>
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-1.5 text-sm font-medium transition ${
              viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            🃏 Cards
          </button>
        </div>
      </div>

      {/* Status Filter — shown in both views */}
      <div className="mb-4 flex items-center flex-wrap gap-2">
        <button
          onClick={() => setFilterType('all')}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
            filterType === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({safeGaps.length})
        </button>
        <button
          onClick={() => setFilterType('active')}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
            filterType === 'active' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setFilterType('resolved')}
          className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
            filterType === 'resolved' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Resolved ({resolvedCount})
        </button>
      </div>

      {/* ── CARD VIEW ─────────────────────────────────────────── */}
      {viewMode === 'cards' && (
        <GapCardView
          gaps={filteredGaps}
          onGapClick={onGapClick}
          filterType={filterType}
        />
      )}

      {/* ── LIST VIEW ─────────────────────────────────────────── */}
      {viewMode === 'list' && (
        <>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search gaps..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* Display Limit — list view only */}
          <div className="mb-4 flex items-center justify-end gap-2">
            <span className="text-sm text-gray-600">Show:</span>
            {[10, 20, 30].map(n => (
              <button
                key={n}
                onClick={() => setDisplayLimit(n)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                  displayLimit === n ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Top {n}
              </button>
            ))}
            <button
              onClick={() => setDisplayLimit('all')}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition ${
                displayLimit === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({filteredGaps.length})
            </button>
          </div>

          {/* Filter by Resource Type */}
          {topResourceTypes.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 mb-2">Filter by Resource Type</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {topResourceTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setSearchTerm(type)}
                    className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium flex items-center justify-between"
                  >
                    <span className="truncate">{type}</span>
                    <span className="ml-2 px-2 py-0.5 bg-blue-600 text-white rounded-full text-xs">
                      {safeResourceSummary[type]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gap Rows */}
          <div
            className={`space-y-3 ${displayLimit === 'all' || displayLimit >= 20 ? 'max-h-[600px] overflow-y-auto pr-2' : ''}`}
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #F7FAFC' }}
          >
            {displayedGaps.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No gaps found matching your filters.</p>
              </div>
            ) : (
              displayedGaps.map((gap, index) => (
                <div key={gap.gap_id || index} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-600 font-bold text-sm flex-shrink-0 mt-0.5">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-semibold text-gray-900 mb-2 cursor-pointer hover:text-blue-600"
                          onClick={() => onGapClick(gap)}
                        >
                          {gap.gap_description}
                        </p>
                        <div className="flex items-center flex-wrap gap-3 text-xs text-gray-600">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                            gap.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {gap.status === 'resolved' ? 'Resolved' : 'Active'}
                          </span>
                          <span className="flex items-center space-x-1">
                            <Package className="w-3.5 h-3.5 text-gray-400" />
                            <span>{gap.resource_type}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Shield className="w-3.5 h-3.5 text-gray-400" />
                            <span>{gap.nims_capability}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className={`flex flex-col items-center justify-center rounded-lg px-3 py-2 min-w-[60px] border ${
                        gap.frequency >= 5 ? 'bg-red-50 border-red-300' :
                        gap.frequency >= 3 ? 'bg-orange-50 border-orange-300' :
                        gap.frequency >= 2 ? 'bg-yellow-50 border-yellow-300' :
                        'bg-blue-50 border-blue-200'
                      }`}>
                        <span className={`text-2xl font-bold leading-none ${
                          gap.frequency >= 5 ? 'text-red-600' :
                          gap.frequency >= 3 ? 'text-orange-600' :
                          gap.frequency >= 2 ? 'text-yellow-600' :
                          'text-blue-600'
                        }`}>
                          {gap.frequency}
                        </span>
                        <span className={`text-xs mt-0.5 whitespace-nowrap ${
                          gap.frequency >= 5 ? 'text-red-500' :
                          gap.frequency >= 3 ? 'text-orange-500' :
                          gap.frequency >= 2 ? 'text-yellow-500' :
                          'text-blue-500'
                        }`}>
                          {gap.frequency === 1 ? 'report' : 'reports'}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2">
                        {gap.status === 'active' ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); onGapClick(gap, true); }}
                            className="px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 transition whitespace-nowrap"
                          >
                            Mark Resolved
                          </button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); onStatusUpdate(gap.gap_id, 'active', gap); }}
                            className="px-3 py-1.5 bg-yellow-600 text-white text-xs font-medium rounded-lg hover:bg-yellow-700 transition whitespace-nowrap"
                          >
                            Reopen
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); onGapClick(gap); }}
                          className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition whitespace-nowrap"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {filteredGaps.length > 10 && (
            <div className="mt-4 text-center text-sm text-gray-600">
              Showing {displayedGaps.length} of {filteredGaps.length} gaps
            </div>
          )}
          <div className="mt-6 text-center text-sm text-gray-500">
            {safeGaps.length} Gaps Found
          </div>
        </>
      )}
    </Widget>
  );
}

function GapDetailsModal({ gap, details, loading, markingAsResolved, onClose, onMarkAsResolved, onMarkAsActive, onSetActiveView}) {  
  const [notes, setNotes] = useState(gap?.status_notes || '');
  const [showNotesInput, setShowNotesInput] = useState(false);

  if (!gap) return null;

  const isResolved = gap.status === 'resolved';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">Gap Details</h2>
                {isResolved ? (
                  <span className="px-3 py-1 bg-green-600 text-white rounded-full text-sm font-medium flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Resolved
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Active
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                Found in {gap.frequency} incident{gap.frequency !== 1 ? 's' : ''}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 hover:bg-white rounded-full transition">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Gap Description
            </h3>
            <p className="text-blue-800 text-sm leading-relaxed">{gap.gap_description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <h4 className="text-xs font-semibold text-purple-700 mb-2 uppercase">Resource Type</h4>
              <p className="text-purple-900 font-medium">{gap.resource_type}</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <h4 className="text-xs font-semibold text-indigo-700 mb-2 uppercase">NIMS Capability</h4>
              <p className="text-indigo-900 font-medium">{gap.nims_capability}</p>
            </div>
          </div>

          {isResolved && gap.status_notes && (
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Resolution Notes
              </h3>
              <p className="text-green-800 text-sm">{gap.status_notes}</p>
              {gap.status_updated_at && (
                <p className="text-xs text-green-600 mt-2">
                  Resolved on {new Date(gap.status_updated_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Affected Incidents ({gap.occurrences?.length || 0})
            </h3>
            {gap.occurrences && gap.occurrences.length > 0 ? (
              <div className="space-y-2">
                {gap.occurrences.map((occurrence, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{occurrence.incident_name || occurrence.aar_id}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(occurrence.date).toLocaleDateString()}
                          {occurrence.is_historic && (
                            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">Historic</span>
                          )}
                        </p>
                      </div>
                      <button onClick={() => { onSetActiveView('reports'); onClose(); }} className="text-blue-600 hover:text-blue-800 text-sm font-medium">View →</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">No incident data available</p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t bg-gray-50">
          {isResolved ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">This gap has been marked as resolved. Click below to reactivate it.</p>
              <div className="flex items-center space-x-3">
                <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition">Close</button>
                <button onClick={() => onMarkAsActive(gap)} disabled={markingAsResolved} className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                  {markingAsResolved ? (<><Activity className="w-4 h-4 animate-spin" /><span>Updating...</span></>) : (<><RefreshCw className="w-4 h-4" /><span>Mark as Active</span></>)}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {!showNotesInput ? (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Mark this gap as resolved once you've taken action to address it.</p>
                  <div className="flex items-center space-x-3">
                    <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition">Close</button>
                    <button onClick={() => setShowNotesInput(true)} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4" /><span>Mark as Resolved</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes (Optional)</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Describe how this gap was resolved (e.g., 'Purchased 5 new radios' or 'Completed training for all staff')" rows={3} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <div className="flex items-center justify-end space-x-3 mt-4">
                    <button onClick={() => { setShowNotesInput(false); setNotes(gap?.status_notes || ''); }} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition" disabled={markingAsResolved}>Cancel</button>
                    <button onClick={() => onMarkAsResolved(gap, notes)} disabled={markingAsResolved} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
                      {markingAsResolved ? (<><Activity className="w-4 h-4 animate-spin" /><span>Saving...</span></>) : (<><CheckCircle className="w-4 h-4" /><span>Confirm Resolution</span></>)}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Hazard Trends Area Chart - Stacked area showing hazard types over time with year filter
function HazardTrendsChart({ data }) {
  const [selectedYear, setSelectedYear] = useState('all');

  if (!data || !data.time_periods || !data.series || !data.series.some(s => s.name !== 'No Data')) {
    return (
      <Widget title="Hazard Trends Over Time" subtitle="Monthly activity by hazard type" icon={<TrendingUp className="w-5 h-5" />}>
        <div className="text-center py-12">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500">No activity data available yet</p>
          <p className="text-xs text-gray-400 mt-1">Create events to see trends here</p>
        </div>
      </Widget>
    );
  }

  const HAZARD_COLORS = {
    // Natural Hazards - warm/earth tones
    'Earthquake': '#B25C3E', 'earthquake': '#B25C3E',
    'Flood': '#4A90D9', 'flood': '#4A90D9',
    'Hurricane / Typhoon': '#2E7D9E', 'hurricane': '#2E7D9E',
    'Tornado': '#7B8A3E', 'tornado': '#7B8A3E',
    'Wildfire': '#E65100', 'wildfire': '#E65100',
    'Winter Storm': '#78909C', 'winter_storm': '#78909C',
    'Severe Weather': '#5D8AA8', 'severe_weather': '#5D8AA8',
    'Drought': '#C0935E', 'drought': '#C0935E',
    'Tsunami': '#00695C', 'tsunami': '#00695C',
    'Volcanic Eruption': '#D84315', 'volcanic_eruption': '#D84315',
    'Landslide / Mudslide': '#795548', 'landslide': '#795548',
    'Pandemic / Epidemic': '#7CB342', 'pandemic': '#7CB342',
    'Space Weather': '#9575CD', 'space_weather': '#9575CD',
    'Other Natural Hazard': '#A1887F', 'natural_other': '#A1887F',
    'Natural Disaster': '#4A90D9', 'natural': '#4A90D9',
    // Technological Hazards - industrial/cool tones
    'Hazardous Materials Release': '#F4511E', 'hazmat_release': '#F4511E',
    'Industrial Accident': '#FF8A65', 'industrial_accident': '#FF8A65',
    'Dam / Levee Failure': '#5C6BC0', 'dam_failure': '#5C6BC0',
    'Infrastructure Failure': '#546E7A', 'infrastructure_failure': '#546E7A',
    'Pipeline Explosion': '#EF6C00', 'pipeline_explosion': '#EF6C00',
    'Radiological Release': '#EC407A', 'radiological_release': '#EC407A',
    'Transportation Accident': '#8D6E63', 'transportation_accident': '#8D6E63',
    'Train Derailment': '#6D4C41', 'train_derailment': '#6D4C41',
    'Mine Accident': '#4E342E', 'mine_accident': '#4E342E',
    'Other Technological Hazard': '#90A4AE', 'technological_other': '#90A4AE',
    'Technological': '#FF8A65', 'technological': '#FF8A65',
    'Radiological': '#EC407A', 'radiological': '#EC407A',
    // Human-Caused Incidents - bold/alert tones
    'Active Shooter / Assailant': '#C62828', 'active_shooter': '#C62828',
    'Terrorism / IED': '#AD1457', 'terrorism_ied': '#AD1457',
    'Cyber Attack': '#283593', 'cyber_attack': '#283593',
    'CBRNE Attack': '#6A1B9A', 'cbrne': '#6A1B9A',
    'Arson / Incendiary': '#BF360C', 'arson': '#BF360C',
    'Civil Unrest': '#AB47BC', 'civil_unrest': '#AB47BC',
    'Sabotage': '#4A148C', 'sabotage': '#4A148C',
    'Mass Gathering Incident': '#26A69A', 'mass_gathering': '#26A69A',
    'Other Human-Caused Incident': '#78909C', 'human_caused_other': '#78909C',
    'Terrorism': '#AD1457', 'terrorism': '#AD1457',
    'Cyber': '#283593', 'cyber': '#283593',
    'Biological': '#7CB342', 'biological': '#7CB342',
    'Chemical': '#C0935E', 'chemical': '#C0935E',
    // Catch-all
    'Other': '#90A4AE', 'other': '#90A4AE',
    'Unknown': '#BDBDBD', 'unknown': '#BDBDBD',
  };
  const DEFAULT_COLORS = ['#4A90D9', '#E65100', '#7CB342', '#5C6BC0', '#C62828', '#26A69A', '#EC407A', '#C0935E', '#AB47BC', '#78909C'];
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const HAZARD_DISPLAY_MAP = {
    'earthquake': 'Earthquake', 'flood': 'Flood', 'hurricane': 'Hurricane', 'tornado': 'Tornado',
    'wildfire': 'Wildfire', 'winter_storm': 'Winter Storm', 'severe_weather': 'Severe Weather',
    'drought': 'Drought', 'tsunami': 'Tsunami', 'volcanic_eruption': 'Volcanic Eruption',
    'landslide': 'Landslide', 'pandemic': 'Pandemic', 'space_weather': 'Space Weather',
    'natural_other': 'Other Natural', 'natural': 'Natural Disaster',
    'hazmat_release': 'Hazmat Release', 'industrial_accident': 'Industrial Accident',
    'dam_failure': 'Dam Failure', 'infrastructure_failure': 'Infrastructure Failure',
    'pipeline_explosion': 'Pipeline Explosion', 'radiological_release': 'Radiological Release',
    'transportation_accident': 'Transportation Accident', 'train_derailment': 'Train Derailment',
    'mine_accident': 'Mine Accident', 'technological_other': 'Other Technological',
    'technological': 'Technological', 'radiological': 'Radiological',
    'active_shooter': 'Active Shooter', 'terrorism_ied': 'Terrorism / IED',
    'cyber_attack': 'Cyber Attack', 'cbrne': 'CBRNE', 'arson': 'Arson',
    'civil_unrest': 'Civil Unrest', 'sabotage': 'Sabotage',
    'mass_gathering': 'Mass Gathering', 'human_caused_other': 'Other Human-Caused',
    'terrorism': 'Terrorism', 'cyber': 'Cyber Attack', 'biological': 'Biological',
    'chemical': 'Chemical', 'other': 'Other', 'unknown': 'Unknown',
  };
  const formatHazardName = (name) => HAZARD_DISPLAY_MAP[name] || name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const activeSeries = data.series
    .filter(s => s.name !== 'No Data' && s.data.some(v => v > 0))
    .map(s => ({ ...s, name: formatHazardName(s.name) }));

  // Extract available years from time_periods
  const availableYears = [...new Set(data.time_periods.map(p => p.split('-')[0]))].sort();

  // Build chart data based on selected year
  let chartData;

  if (selectedYear === 'all') {
    // Aggregate all years into a single Jan-Dec view
    const monthTotals = {};
    MONTH_NAMES.forEach(m => { monthTotals[m] = {}; activeSeries.forEach(s => { monthTotals[m][s.name] = 0; }); });

    data.time_periods.forEach((period, idx) => {
      const monthIdx = parseInt(period.split('-')[1]) - 1;
      const monthLabel = MONTH_NAMES[monthIdx];
      if (monthLabel) {
        activeSeries.forEach(s => {
          monthTotals[monthLabel][s.name] += (s.data[idx] || 0);
        });
      }
    });

    chartData = MONTH_NAMES.map(month => ({ month, ...monthTotals[month] }));
  } else {
    // Filter to specific year
    const yearPeriods = data.time_periods
      .map((period, idx) => ({ period, idx }))
      .filter(({ period }) => period.startsWith(selectedYear));

    // Build all 12 months even if some are empty
    chartData = MONTH_NAMES.map((month, mIdx) => {
      const point = { month };
      const matchPeriod = yearPeriods.find(({ period }) => {
        const periodMonth = parseInt(period.split('-')[1]) - 1;
        return periodMonth === mIdx;
      });
      activeSeries.forEach(s => {
        point[s.name] = matchPeriod ? (s.data[matchPeriod.idx] || 0) : 0;
      });
      return point;
    });
  }

  const totalEvents = chartData.reduce((sum, point) =>
    sum + activeSeries.reduce((s, sr) => s + (point[sr.name] || 0), 0), 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
    if (total === 0) return null;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3" style={{ minWidth: 160 }}>
        <p className="text-sm font-semibold text-gray-900 mb-2 border-b pb-1">{label}{selectedYear !== 'all' ? ` ${selectedYear}` : ' (All Years)'}</p>
        {payload.filter(p => p.value > 0).reverse().map((entry, idx) => (
          <div key={idx} className="flex items-center justify-between gap-4 py-0.5">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
              <span className="text-xs text-gray-700">{entry.name}</span>
            </div>
            <span className="text-xs font-semibold text-gray-900">{entry.value}</span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 pt-1 mt-1 border-t">
          <span className="text-xs font-medium text-gray-600">Total</span>
          <span className="text-xs font-bold text-gray-900">{total}</span>
        </div>
      </div>
    );
  };

  return (
    <Widget
      title="Hazard Trends Over Time"
      subtitle={`${totalEvents} events ${selectedYear === 'all' ? 'across all years' : `in ${selectedYear}`}`}
      icon={<TrendingUp className="w-5 h-5" />}
    >
      <div className="space-y-4">
        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSelectedYear('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
              selectedYear === 'all'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >All Years</button>
          {availableYears.map(year => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                selectedYear === year
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >{year}</button>
          ))}
        </div>

        {/* Area Chart */}
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
              <defs>
                {activeSeries.map((s, i) => {
                  const color = HAZARD_COLORS[s.name] || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
                  return (
                    <linearGradient key={`grad-${i}`} id={`hazGrad-${i}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.7} />
                      <stop offset="100%" stopColor={color} stopOpacity={0.3} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: '#555', fontWeight: 500 }}
                axisLine={{ stroke: '#ddd' }}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: '#888' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }}
                iconType="square"
                iconSize={10}
              />
              {activeSeries.map((s, i) => {
                const color = HAZARD_COLORS[s.name] || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
                return (
                  <Area
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stackId="hazards"
                    stroke={color}
                    strokeWidth={1.5}
                    fill={`url(#hazGrad-${i})`}
                    activeDot={{ r: 3, strokeWidth: 1.5, stroke: '#fff', fill: color }}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Widget>
  );
}

// Hazard Preparedness Widget
function HazardPreparednessWidget({ data }) {
  if (!data || !data.hazard_preparedness) return null;

  // Filter out hazards with zero total events
  const activeHazards = data.hazard_preparedness.filter(h => h.total_events > 0);

  if (activeHazards.length === 0) {
    return (
      <Widget title="Exercise vs Incident Balance" subtitle="Training readiness by hazard type" icon={<Shield className="w-5 h-5" />}>
        <div className="text-center py-8 text-gray-500">
          <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No hazard data available yet</p>
        </div>
      </Widget>
    );
  }

  const BalanceBar = ({ hazard }) => {
    const exercises = hazard.exercises || 0;
    const incidents = hazard.incidents || 0;
    const total = exercises + incidents;

    // Calculate position: 0 = all incidents (left), 0.5 = balanced, 1 = all exercises (right)
    // Ideal ratio is ~1 exercise per 3 incidents = 25% exercises
    let position;
    if (total === 0) {
      position = 0.5;
    } else if (incidents === 0 && exercises > 0) {
      position = Math.min(0.75 + (exercises / 10) * 0.25, 0.95);
    } else if (exercises === 0 && incidents > 0) {
      position = Math.max(0.15 - (incidents / 10) * 0.1, 0.05);
    } else {
      const exerciseRatio = exercises / total;
      if (exerciseRatio <= 0.25) {
        position = 0.15 + (exerciseRatio / 0.25) * 0.35;
      } else {
        position = 0.5 + ((exerciseRatio - 0.25) / 0.75) * 0.35;
      }
    }
    position = Math.max(0.05, Math.min(0.95, position));

    // Status label and colors based on balance
    const distFromCenter = Math.abs(position - 0.5);
    let status, statusColor, statusBg, markerColor;
    if (distFromCenter < 0.1) {
      status = 'Balanced';
      statusColor = '#166534';
      statusBg = '#dcfce7';
      markerColor = '#16a34a';
    } else if (distFromCenter < 0.2 && position < 0.5) {
      status = 'Needs exercises';
      statusColor = '#92400e';
      statusBg = '#fef3c7';
      markerColor = '#d97706';
    } else if (distFromCenter < 0.2 && position >= 0.5) {
      status = 'Exercise-heavy';
      statusColor = '#1e40af';
      statusBg = '#dbeafe';
      markerColor = '#2563eb';
    } else if (position < 0.5) {
      status = 'Under-exercised';
      statusColor = '#991b1b';
      statusBg = '#fee2e2';
      markerColor = '#dc2626';
    } else {
      status = 'Very exercise-heavy';
      statusColor = '#1e3a8a';
      statusBg = '#dbeafe';
      markerColor = '#1d4ed8';
    }

    const exerciseWidth = position * 100;
    const incidentWidth = (1 - position) * 100;

    return (
      <div className="py-2.5" role="figure" aria-label={`${hazard.hazard_type}: ${exercises} exercises, ${incidents} incidents — ${status}`}>
        {/* Header row */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-semibold text-gray-900">{hazard.hazard_type}</span>
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ color: statusColor, backgroundColor: statusBg }}
          >{status}</span>
        </div>
        {/* Count labels */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-gray-600">Exercises ({exercises})</span>
          <span className="text-xs font-medium text-gray-600">Incidents ({incidents})</span>
        </div>
        {/* Balance bar */}
        <div className="relative flex h-4 rounded border border-gray-300 overflow-hidden" role="img" aria-label={`Balance: ${exercises} exercises to ${incidents} incidents`}>
          {/* Exercise side - green */}
          <div
            style={{ width: `${exerciseWidth}%`, backgroundColor: '#22c55e' }}
            className="h-full"
          />
          {/* Incident side - red */}
          <div
            style={{ width: `${incidentWidth}%`, backgroundColor: '#ef4444' }}
            className="h-full"
          />
          {/* Center marker line */}
          <div
            className="absolute top-0 h-full"
            style={{
              left: `${exerciseWidth}%`,
              transform: 'translateX(-50%)',
              width: '3px',
              backgroundColor: '#1f2937',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <Widget title="Exercise vs Incident Balance" subtitle="Training readiness by hazard type" icon={<Shield className="w-5 h-5" />}>
      <div className="space-y-0">
        {activeHazards.slice(0, 10).map((hazard, idx) => (
          <BalanceBar key={idx} hazard={hazard} />
        ))}

        {/* Legend */}
        <div className="bg-gray-50 rounded-lg p-3 border border-gray-300 mt-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-3 rounded border border-gray-300" style={{ backgroundColor: '#22c55e' }} />
              <span className="text-gray-700 font-medium">Exercises (Drills, Trainings, Exercises)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-3 rounded border border-gray-300" style={{ backgroundColor: '#ef4444' }} />
              <span className="text-gray-700 font-medium">Incidents (Real-World Incidents, Events/Mass Gatherings)</span>
            </div>
          </div>
          <p className="text-xs text-gray-600 text-center">Target: ~1 exercise for every 3 real incidents</p>
        </div>
      </div>
    </Widget>
  );
}

// Capability Heatmap with Mission Area Grouping
function CapabilityGapsHeatmap() {
  const { organization } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState(null);
  const [selectedIncidentType, setSelectedIncidentType] = useState('all');
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [expandedMissionAreas, setExpandedMissionAreas] = useState({ prevention: true});

  useEffect(() => {
    if (organization?.organizationId) {
      loadHeatmapData();
    }
  }, [organization]);

  const toggleMissionArea = (key) => {
    setExpandedMissionAreas(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const loadHeatmapData = async () => {
    if (!organization?.organizationId) return;
    try {
      const data = await fetchWithAuth('/dashboard/capability-heatmap', organization.organizationId);
      setHeatmapData(data);
      const types = ['all', ...new Set(data.incidents.map(i => i.incident_type))];
      setIncidentTypes(types);
    } catch (error) {
      console.error('Error loading heatmap:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Widget title="FEMA Capability Analysis" subtitle="Loading capability data...">
        <div className="flex items-center justify-center py-12">
          <Activity className="w-8 h-8 text-blue-500 animate-spin mr-3" />
          <p className="text-gray-600">Analyzing capabilities...</p>
        </div>
      </Widget>
    );
  }

  if (!heatmapData || !heatmapData.incidents) {
    return (
      <Widget title="FEMA Capability Analysis" subtitle="No capability data available">
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No incidents with capability scores found</p>
        </div>
      </Widget>
    );
  }

  const filteredIncidents = selectedIncidentType === 'all' ? heatmapData.incidents : heatmapData.incidents.filter(i => i.incident_type === selectedIncidentType);
  const capabilitiesData = {};
  Object.values(MISSION_AREAS).forEach(area => { area.capabilities.forEach(capName => { capabilitiesData[capName] = { avg_score: 0, count: 0, scores: [] }; }); });
  filteredIncidents.forEach(incident => { if (incident.capability_scores) { Object.entries(incident.capability_scores).forEach(([capName, score]) => { if (capabilitiesData[capName] && score > 0) { capabilitiesData[capName].scores.push(score); } }); } });
  Object.keys(capabilitiesData).forEach(capName => { const scores = capabilitiesData[capName].scores; if (scores.length > 0) { capabilitiesData[capName].avg_score = scores.reduce((sum, s) => sum + s, 0) / scores.length; capabilitiesData[capName].count = scores.length; } });
  const groupedCapabilities = groupCapabilitiesByMissionArea(capabilitiesData);

  const allScores = Object.values(capabilitiesData).filter(cap => cap.count > 0 && cap.avg_score > 0).map(cap => cap.avg_score);
  const criticalGaps = allScores.filter(score => score < 60).length;
  const needsImprovement = allScores.filter(score => score >= 60 && score < 75).length;
  const adequate = allScores.filter(score => score >= 75 && score < 85).length;
  const strong = allScores.filter(score => score >= 85).length;

  return (
    <Widget title="FEMA Core Capabilities Analysis" subtitle={`Showing ${filteredIncidents.length} incident${filteredIncidents.length !== 1 ? 's' : ''}${selectedIncidentType !== 'all' ? ` (${selectedIncidentType})` : ''}`}>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pb-4 border-b">
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Filter by Incident Type:</label>
            <select value={selectedIncidentType} onChange={(e) => setSelectedIncidentType(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              {incidentTypes.map(type => (<option key={type} value={type}>{type === 'all' ? 'All Incident Types' : type}</option>))}
            </select>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center space-x-2"><div className="w-4 h-4 bg-red-500 rounded"></div><span className="text-gray-600">Critical (&lt;60)</span></div>
            <div className="flex items-center space-x-2"><div className="w-4 h-4 bg-orange-500 rounded"></div><span className="text-gray-600">Needs Work (60-74)</span></div>
            <div className="flex items-center space-x-2"><div className="w-4 h-4 bg-yellow-500 rounded"></div><span className="text-gray-600">Adequate (75-84)</span></div>
            <div className="flex items-center space-x-2"><div className="w-4 h-4 bg-green-500 rounded"></div><span className="text-gray-600">Strong (85+)</span></div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center"><div className="text-3xl font-bold text-red-600">{criticalGaps}</div><div className="text-sm text-red-700 font-medium mt-1">Critical Gaps</div></div>
          <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 text-center"><div className="text-3xl font-bold text-orange-600">{needsImprovement}</div><div className="text-sm text-orange-700 font-medium mt-1">Needs Improvement</div></div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-center"><div className="text-3xl font-bold text-yellow-600">{adequate}</div><div className="text-sm text-yellow-700 font-medium mt-1">Adequate</div></div>
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 text-center"><div className="text-3xl font-bold text-green-600">{strong}</div><div className="text-sm text-green-700 font-medium mt-1">Strong</div></div>
        </div>
        <div className="space-y-3">
          {Object.entries(MISSION_AREAS).map(([key, missionArea]) => (
            <CollapsibleMissionAreaSection key={key} missionAreaKey={key} missionAreaData={missionArea} capabilities={groupedCapabilities[key]} isExpanded={expandedMissionAreas[key] || false} onToggle={() => toggleMissionArea(key)} />
          ))}
        </div>
      </div>
    </Widget>
  );
}

function AnalyticsView({ onNavigate }) {
  const { organization } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [gapsData, setGapsData] = useState(null);
  const [capabilityHeatmap, setCapabilityHeatmap] = useState(null);
  const [hazardTrends, setHazardTrends] = useState(null);
  const [hazardPreparedness, setHazardPreparedness] = useState(null);
  const [strengthsData, setStrengthsData] = useState(null);
  const [strengthsLimit, setStrengthsLimit] = useState(10);
  const [selectedGap, setSelectedGap] = useState(null);
  const [showGapModal, setShowGapModal] = useState(false);
  const [gapDetails, setGapDetails] = useState(null);
  const [loadingGapDetails, setLoadingGapDetails] = useState(false);
  const [markingAsResolved, setMarkingAsResolved] = useState(false);
  const [showResolvedGaps, setShowResolvedGaps] = useState(false);
  const [quickResolveGap, setQuickResolveGap] = useState(null);
  const [quickResolveNotes, setQuickResolveNotes] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '2020-01-01',
    endDate: new Date().toISOString().split('T')[0]
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [quickRanges] = useState([
    { label: 'Last 30 Days', days: 30 },
    { label: 'Last 90 Days', days: 90 },
    { label: 'Last 6 Months', days: 180 },
    { label: 'Last Year', days: 365 },
    { label: 'All Time', days: null }
  ]);

  const applyQuickRange = (days) => {
    if (days === null) {
      setDateRange({ startDate: '2020-01-01', endDate: new Date().toISOString().split('T')[0] });
    } else {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      setDateRange({ startDate: startDate.toISOString().split('T')[0], endDate: endDate.toISOString().split('T')[0] });
    }
    setShowDatePicker(false);
  };

  useEffect(() => {
    if (organization?.organizationId) { loadAnalyticsData(); }
  }, [organization, dateRange]);

  const loadAnalyticsData = async () => {
    if (!organization?.organizationId) return;
    setLoading(true);
    try {
      const dateParams = `&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`;
      const [gaps, matrix, hazTrends, hazards, strengths] = await Promise.all([
        fetchWithAuth(`/dashboard/gaps?organization_id=${organization.organizationId}${dateParams}`, organization.organizationId),
        fetchWithAuth(`/dashboard/capability-heatmap?organization_id=${organization.organizationId}${dateParams}`, organization.organizationId),
        fetchWithAuth(`/dashboard/trends?type=hazard_type&range=60&organization_id=${organization.organizationId}`, organization.organizationId),
        fetchWithAuth(`/dashboard/hazard-preparedness?organization_id=${organization.organizationId}${dateParams}`, organization.organizationId),
        fetchWithAuth(`/dashboard/strengths?organization_id=${organization.organizationId}`, organization.organizationId)
      ]);
      setGapsData(gaps);
      setCapabilityHeatmap(matrix);
      setHazardTrends(hazTrends);
      setHazardPreparedness(hazards);
      setStrengthsData(strengths);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleGapClick = async (gap, showResolve = false) => {
    if (showResolve) {
      setQuickResolveGap(gap);
      setQuickResolveNotes('');
      return;
    }
    setShowGapModal(true);
    setLoadingGapDetails(true);
    try {
      const incidentsWithNames = await Promise.all(
        gap.occurrences.map(async (occurrence) => {
          try {
            const metadata = await fetchWithAuth(`/dashboard/reports`, organization.organizationId);
            const report = metadata.reports?.find(r => r.aar_id === occurrence.aar_id);
            return { ...occurrence, incident_name: report?.incident_name || occurrence.aar_id };
          } catch (error) {
            console.error(`Error fetching incident name for ${occurrence.aar_id}:`, error);
            return { ...occurrence, incident_name: occurrence.aar_id };
          }
        })
      );
      setSelectedGap({ ...gap, occurrences: incidentsWithNames });
    } catch (error) {
      console.error('Error loading gap details:', error);
      toast.error('Failed to load gap details');
      setSelectedGap(gap);
    } finally {
      setLoadingGapDetails(false);
    }
  };

  const handleMarkAsResolved = async (gap, notes = '') => {
    setMarkingAsResolved(true);
    try {
      const url = `${API_ENDPOINT}/dashboard/gaps/${gap.gap_id}?organization_id=${organization.organizationId}`;
      const body = {
        action: 'update_status', status: 'resolved', organization_id: organization.organizationId,
        gap_description: gap.gap_description, resource_type: gap.resource_type,
        nims_capability: gap.nims_capability, frequency: gap.frequency, notes: notes
      };
      const response = await authenticatedFetch(url, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Gap marked as resolved!');
      setShowGapModal(false);
      await loadAnalyticsData();
    } catch (error) {
      console.error('Error marking gap as resolved:', error);
      toast.error('Failed to mark gap as resolved');
    } finally {
      setMarkingAsResolved(false);
    }
  };

  const handleQuickResolve = async () => {
    if (!quickResolveGap) return;
    setMarkingAsResolved(true);
    try {
      const gap = quickResolveGap;
      const url = `${API_ENDPOINT}/dashboard/gaps/${gap.gap_id}?organization_id=${organization.organizationId}`;
      const body = {
        action: 'update_status', status: 'resolved', organization_id: organization.organizationId,
        gap_description: gap.gap_description, resource_type: gap.resource_type,
        nims_capability: gap.nims_capability, frequency: gap.frequency, notes: quickResolveNotes
      };
      await authenticatedFetch(url, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('Gap marked as resolved!');
      setQuickResolveGap(null);
      setQuickResolveNotes('');
      await loadAnalyticsData();
    } catch (error) {
      console.error('Error marking gap as resolved:', error);
      toast.error('Failed to mark gap as resolved');
    } finally {
      setMarkingAsResolved(false);
    }
  };

  const handleMarkAsActive = async (gap) => {
    setMarkingAsResolved(true);
    try {
      await authenticatedFetch(
        `${API_ENDPOINT}/dashboard/gaps/${gap.gap_id}?organization_id=${organization.organizationId}`,
        { method: 'PUT', body: JSON.stringify({ action: 'update_status', status: 'active', organization_id: organization.organizationId, gap_description: gap.gap_description, resource_type: gap.resource_type, nims_capability: gap.nims_capability, frequency: gap.frequency, notes: '' }) }
      );
      toast.success('Gap marked as active');
      setShowGapModal(false);
      loadAnalyticsData();
    } catch (error) {
      console.error('Error marking gap as active:', error);
      toast.error('Failed to update gap status');
    } finally {
      setMarkingAsResolved(false);
    }
  };

  const handleGapStatusUpdate = async (gapId, newStatus) => {
    setMarkingAsResolved(true);
    try {
      const gap = gapsData?.top_gaps?.find(g => g.gap_id === gapId);
      if (!gap) { toast.error('Gap not found'); return; }
      const url = `${API_ENDPOINT}/dashboard/gaps/${encodeURIComponent(gapId)}?organization_id=${organization.organizationId}`;
      const body = { status: newStatus, organization_id: organization.organizationId, gap_description: gap.gap_description, resource_type: gap.resource_type, nims_capability: gap.nims_capability, frequency: gap.frequency, notes: '' };
      const response = await authenticatedFetch(url, { method: 'PUT', body: JSON.stringify(body) });
      toast.success(`Gap marked as ${newStatus}!`);
      await loadAnalyticsData();
    } catch (error) {
      console.error('Error updating gap status:', error);
      toast.error('Failed to update gap status');
    } finally {
      setMarkingAsResolved(false);
    }
  };

  const handleExportForGrant = () => {
    if (!gapsData || !gapsData.top_gaps) { toast.error('No gap data to export'); return; }
    const activeGaps = gapsData.top_gaps.filter(g => g.status !== 'resolved');
    if (activeGaps.length === 0) { toast.error('No active gaps to export'); return; }
    const headers = ['Gap Description','Resource Type','NIMS Capability','Frequency (# of incidents)','Severity Level','First Identified','Most Recent Occurrence'];
    const rows = activeGaps.map(gap => {
      const occurrences = gap.occurrences || [];
      const dates = occurrences.map(o => o.date).filter(d => d).sort();
      const firstDate = dates[0] || 'Unknown';
      const lastDate = dates[dates.length - 1] || 'Unknown';
      const severity = gap.frequency >= 3 ? 'Critical' : gap.frequency >= 2 ? 'High' : 'Moderate';
      return [gap.gap_description, gap.resource_type, gap.nims_capability, gap.frequency, severity, firstDate, lastDate];
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Grant_Justification_Gaps_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${activeGaps.length} active gaps for grant application!`);
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    const downloadToast = toast.loading('Generating analytics report...');
    try {
      const data = await fetchWithAuth(
        `/dashboard/analytics-report?start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`,
        organization.organizationId
      );
      toast.success('Report generated! Starting download...', { id: downloadToast, icon: '📊', duration: 3000 });
      window.open(data.download_url, '_blank');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate analytics report', { id: downloadToast });
    } finally {
      setDownloadingReport(false);
    }
  };

  const DateRangeFilter = () => {
    const activeLabel = quickRanges.find(r => {
      if (r.days === null) return dateRange.startDate === '2020-01-01';
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - r.days);
      return dateRange.startDate === start.toISOString().split('T')[0] && dateRange.endDate === end.toISOString().split('T')[0];
    })?.label || 'Custom';

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Calendar className="w-5 h-5 text-gray-400" />
            {quickRanges.map((range) => (
              <button
                key={range.label}
                onClick={() => applyQuickRange(range.days)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                  activeLabel === range.label
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >{range.label}</button>
            ))}
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
                activeLabel === 'Custom'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >{showDatePicker ? 'Close' : 'Custom Range'}</button>
          </div>
          <button
            onClick={handleDownloadReport}
            disabled={downloadingReport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {downloadingReport ? 'Generating...' : 'Download Report'}
          </button>
        </div>
        {showDatePicker && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} max={dateRange.endDate} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} min={dateRange.startDate} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => setShowDatePicker(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={() => { setShowDatePicker(false); loadAnalyticsData(); }} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Apply</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Activity className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">Loading analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DateRangeFilter />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <HazardTrendsChart data={hazardTrends} />
        <HazardPreparednessWidget data={hazardPreparedness} />
      </div>
      <CapabilityGapsHeatmap />
      <CapabilityGapsAnalysisRefined 
        gaps={gapsData?.top_gaps}
        resourceSummary={gapsData?.resource_summary}
        capabilitySummary={gapsData?.capability_summary}
        onGapClick={handleGapClick}
        onStatusUpdate={handleGapStatusUpdate}
      />

      {/* Operational Strengths */}
      {strengthsData?.top_strengths && strengthsData.top_strengths.length > 0 && (
        <Widget
          title="Operational Strengths"
          subtitle={`${strengthsData.total_strengths_identified || 0} strengths identified across ${strengthsData.total_reports_analyzed || 0} reports`}
          icon={<CheckCircle className="w-5 h-5" />}
        >
          {/* Display Limit Toggle */}
          <div className="mb-4 flex items-center space-x-2">
            <span className="text-sm text-gray-600">Show:</span>
            {[10, 20, 30].map(n => (
              <button key={n} onClick={() => setStrengthsLimit(n)} className={`px-3 py-1 rounded-lg text-sm font-medium transition ${strengthsLimit === n ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Top {n}</button>
            ))}
            <button onClick={() => setStrengthsLimit('all')} className={`px-3 py-1 rounded-lg text-sm font-medium transition ${strengthsLimit === 'all' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>All ({strengthsData.top_strengths.length})</button>
          </div>

          <div className={`space-y-3 ${strengthsLimit === 'all' || strengthsLimit >= 20 ? 'max-h-[600px] overflow-y-auto pr-2' : ''}`} style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #F7FAFC' }}>
            {(strengthsLimit === 'all' ? strengthsData.top_strengths : strengthsData.top_strengths.slice(0, strengthsLimit)).map((strength, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white font-bold text-sm flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{strength.strength_description}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-600">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium">
                      <CheckCircle className="w-3 h-3" />
                      {strength.frequency} occurrence{strength.frequency !== 1 ? 's' : ''}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Shield className="w-3 h-3 text-gray-400" />
                      {strength.nims_capability}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {strengthsData.top_strengths.length > 10 && (
            <div className="mt-4 text-center text-sm text-gray-600">
              Showing {strengthsLimit === 'all' ? strengthsData.top_strengths.length : Math.min(strengthsLimit, strengthsData.top_strengths.length)} of {strengthsData.top_strengths.length} strengths
            </div>
          )}
        </Widget>
      )}

      {showGapModal && selectedGap && (
        <GapDetailsModal
          gap={selectedGap}
          gapDetails={gapDetails}
          loading={loadingGapDetails}
          onClose={() => setShowGapModal(false)}
          onMarkAsResolved={handleMarkAsResolved}
          onMarkAsActive={handleMarkAsActive}
          markingAsResolved={markingAsResolved}
          onSetActiveView={onNavigate}
        />
      )}

      {/* Quick Resolve Popup */}
      {quickResolveGap && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Mark Gap as Resolved</h3>
            <p className="text-sm text-gray-600 mb-4 line-clamp-2">{quickResolveGap.gap_description}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Resolution Notes (Optional)</label>
              <textarea
                value={quickResolveNotes}
                onChange={(e) => setQuickResolveNotes(e.target.value)}
                placeholder="Describe how this gap was resolved (e.g., 'Purchased 5 new radios' or 'Completed training for all staff')"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setQuickResolveGap(null); setQuickResolveNotes(''); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition text-sm font-medium"
                disabled={markingAsResolved}
              >Cancel</button>
              <button
                onClick={handleQuickResolve}
                disabled={markingAsResolved}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {markingAsResolved ? 'Resolving...' : '✓ Confirm Resolution'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnalyticsView;